import { useEffect, useRef } from "react";
import Page from "src/layouts/Page";
import useSectionTitleSettings from "../../hooks/useSectionTitleSettings";
import useSaveSectionTitleSettings from "../../hooks/useSaveSectionTitleSettings";
import { adaptSectionTitleSettingsPayload } from "../../adapters/adaptSectionTitleSettingsPayload";
import SectionTitlesForm from "../../components/SectionTitlesForm/SectionTitlesForm";

const SectionTitles = () => {
	const { data: sections, isLoading, isError } = useSectionTitleSettings();
	const { mutate: saveSettings, isLoading: isSaving } =
		useSaveSectionTitleSettings();

	const initialSectionsRef = useRef<SectionTitleSetting[] | null>(null);
	const isDirtyRef = useRef(false);

	// Track server state as the baseline for dirty detection.
	// Only update when the form is not dirty to avoid overwriting
	// the user's in-progress edits.
	useEffect(() => {
		if (sections && !isDirtyRef.current) {
			initialSectionsRef.current = sections;
		}
	}, [sections]);

	if (isLoading) {
		return (
			<Page>
				<div className="flex items-center justify-center py-20">
					<span className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
				</div>
			</Page>
		);
	}

	if (isError || !sections) {
		return (
			<Page>
				<div className="flex items-center justify-center py-20 text-muted-foreground">
					Failed to load section title settings.
				</div>
			</Page>
		);
	}

	const handleSubmit = (
		data: SectionTitlesFormValues,
		reset: (values: SectionTitlesFormValues) => void
	) => {
		if (!initialSectionsRef.current) return;

		const payload = adaptSectionTitleSettingsPayload(
			data.sections,
			initialSectionsRef.current
		);

		if (payload.length === 0) return;

		saveSettings(payload, {
			onSuccess: () => {
				isDirtyRef.current = false;
				reset(data);
			}
		});
	};

	return (
		<Page>
			<SectionTitlesForm
				defaultValues={{ sections }}
				onSubmit={handleSubmit}
				isSubmitting={isSaving}
				onDirtyChange={(dirty) => {
					isDirtyRef.current = dirty;
				}}
			/>
		</Page>
	);
};

export default SectionTitles;
